import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FCanvasComponent, FCreateConnectionEvent, FReassignConnectionEvent, FZoomDirective } from '@foblex/flow';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { NodeManager } from '@app/utilities/foblex-flow/node-manager';
import { ConnectionManager } from '@app/utilities/foblex-flow/connection-manager';
import { FoblexNode } from '@app/utilities/foblex-flow/base-node/base-node';
import { FlowNodeInteractor } from './nodes.service';
import { DeviceNode } from './device-node/device-node.component';
import { PSDChartNode } from './chart-node/chart-node.component';
import { Device, fragQuery } from '@models';
import { Observable, Subscription, catchError, combineLatest, distinctUntilChanged, map, of, shareReplay, startWith, switchMap } from 'rxjs';
import { REDUCTION_RATIO_PERCENT, ReductionRatioState, createD80ReductionRatio, getAggregateDValue, } from '../reduction-ratio';

interface ReductionRatioConnection {
    upstreamDevice: Device;
    downstreamDevice: Device;
}

interface ReductionRatioContext {
    key: string;
    connections: ReductionRatioConnection[];
}

interface DeviceD80Response {
    device: Device;
    response: fragQuery.timeline.sieveSizesAtPercents.Response | null | undefined;
}

@Component({
    selector: 'nodes',
    templateUrl: './nodes.component.html',
    styleUrls: ['./nodes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,

})
export class NodesComponent implements OnInit, OnDestroy {
    public readonly FoblexNode = FoblexNode;
    public readonly DeviceNode = DeviceNode;
    public readonly PSDChartNode = PSDChartNode;

    @ViewChild(FCanvasComponent, { static: false })
    public fCanvas!: FCanvasComponent;

    @ViewChild(FZoomDirective, { static: false })
    public fZoom!: FZoomDirective;

    public deviceNodes: DeviceNode[] = [];
    public psdChartNodes: PSDChartNode[] = [];
    public readonly connections$ = this.connectionManager.connections$;
    public reductionRatioState$: Observable<ReductionRatioState> = of({
        loading: false,
        connectedDownstreamDeviceIds: new Set<number>(),
        ratiosByDownstreamDeviceId: new Map<number, never>(),
    });
    public reductionRatioState: ReductionRatioState = {
        loading: false,
        connectedDownstreamDeviceIds: new Set<number>(),
        ratiosByDownstreamDeviceId: new Map<number, never>(),
    };
    private readonly subscriptions = new Subscription();

    @Output()
    public readonly connectionReassigned = new EventEmitter<FReassignConnectionEvent>();

    constructor(
        private readonly fragQuery: FragQueryService,
        private connectionManager: ConnectionManager,
        public nodeManager: NodeManager,
        public readonly flowNodeInteractor: FlowNodeInteractor,
        private cd: ChangeDetectorRef
    ) { }

    public ngOnInit() {
        this.connectionManager.loadInitialConnections();
        this.flowNodeInteractor.initializeNodes();

        this.reductionRatioState$ = combineLatest([
            this.nodeManager.nodes$,
            this.connectionManager.connections$,
        ]).pipe(
            map(([nodes, connections]): ReductionRatioContext => {
                const deviceNodes = nodes.filter(DeviceNode.isDeviceNode);
                const ratioConnections = connections.flatMap(connection => {
                    const upstreamNode = deviceNodes.find(node => node.outputId === connection.from);
                    const downstreamNode = deviceNodes.find(node => node.inputId === connection.to);
                    
                    if (!upstreamNode || !downstreamNode || upstreamNode.device.id === downstreamNode.device.id) {
                        return [];
                    }

                    return [{
                        upstreamDevice: upstreamNode.device,
                        downstreamDevice: downstreamNode.device,
                    }];
                });

                const uniqueConnections = ratioConnections.filter((connection, index, all) =>
                    all.findIndex(candidate =>
                        candidate.upstreamDevice.id === connection.upstreamDevice.id
                        && candidate.downstreamDevice.id === connection.downstreamDevice.id
                    ) === index
                );

                const key = uniqueConnections
                    .map(connection => `${connection.upstreamDevice.id}>${connection.downstreamDevice.id}`)
                    .sort()
                    .join('|');

                return { key, connections: uniqueConnections };
            }),
            
            distinctUntilChanged((previous, current) => previous.key === current.key),
            switchMap(context => this.loadReductionRatios(context.connections)),
            shareReplay({ bufferSize: 1, refCount: true }),
        );

        this.subscriptions.add(this.reductionRatioState$.subscribe(state => {
            this.reductionRatioState = state;
            this.cd.markForCheck();
        }));

        this.subscriptions.add(this.nodeManager.nodes$.subscribe(nodes => {
            this.deviceNodes = [];
            this.psdChartNodes = [];
            nodes.forEach(node => {
                if (DeviceNode.isDeviceNode(node)) {
                    this.deviceNodes.push(node);
                } else if (PSDChartNode.isPSDChartNode(node)) {
                    this.psdChartNodes.push(Object.assign(
                        Object.create(Object.getPrototypeOf(node)),
                        node
                    ));
                }
            });
            this.cd.markForCheck();
        }));
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private loadReductionRatios(
        connections: ReductionRatioConnection[],
    ): Observable<ReductionRatioState> {
        if (!connections.length) {
            return of({
                loading: false,
                connectedDownstreamDeviceIds: new Set<number>(),
                ratiosByDownstreamDeviceId: new Map(),
            });
        }

        const devices = connections
            .flatMap(connection => [connection.upstreamDevice, connection.downstreamDevice])
            .filter((device, index, all) =>
                all.findIndex(candidate => candidate.id === device.id) === index
            );

        return combineLatest(devices.map(device =>
            this.fragQuery.getTimelineSieveSizesForDevice(
                device,
                [REDUCTION_RATIO_PERCENT],
            ).pipe(
                map(response => ({ device, response }) as DeviceD80Response),
                catchError(() => of({ device, response: null } as DeviceD80Response)),
                startWith({ device, response: undefined } as DeviceD80Response),
            )
        )).pipe(
            map(deviceResponses => {
                const loading = deviceResponses.some(item => item.response === undefined);
                const connectedDownstreamDeviceIds = new Set(
                    connections.map(connection => connection.downstreamDevice.id)
                );
                const d80ByDeviceId = new Map<number, number | null>(
                    deviceResponses.map(item => [
                        item.device.id,
                        getAggregateDValue(item.response, REDUCTION_RATIO_PERCENT),
                    ])
                );
                const ratiosByDownstreamDeviceId = new Map();

                connections.forEach(connection => {
                    const ratio = createD80ReductionRatio(
                        connection.upstreamDevice,
                        connection.downstreamDevice,
                        d80ByDeviceId.get(connection.upstreamDevice.id) ?? null,
                        d80ByDeviceId.get(connection.downstreamDevice.id) ?? null,
                    );
                    if (ratio) {
                        ratiosByDownstreamDeviceId.set(connection.downstreamDevice.id, ratio);
                    }
                });

                return {
                    loading,
                    connectedDownstreamDeviceIds,
                    ratiosByDownstreamDeviceId,
                };
            }),
        );
    }

    public zoomIn() {
        this.fZoom.zoomIn();
    }

    public zoomOut() {
        this.fZoom.zoomOut();
    }

    public resetZoom() {
        this.fZoom.reset();
    }

    public onNodeChanged(nodeId: number, newPosition: { x: number, y: number }) {
        this.nodeManager.updateNodePosition(nodeId, newPosition);
        this.fragQuery.cacheNodePosition(nodeId, newPosition)
    }

    public async onCreateConnection(event: FCreateConnectionEvent) {
        if (!event.fInputId) return;

        await this.connectionManager.addConnectionOnConnect(event.fOutputId, event.fInputId);
        this.fragQuery.trigger$.next()
    }
}
